// Performance Manager for Fortisys Website
// Handles performance monitoring, optimization, and error handling

export class PerformanceManager {
  constructor(options = {}) {
    this.options = {
      enableMetrics: true,
      enableErrorHandling: true,
      enableResourceHints: true,
      reportingEndpoint: null,
      ...options
    };

    this.metrics = {
      pageLoad: {},
      userInteractions: [],
      errors: []
    };

    this.init();
  }

  init() {
    if (this.options.enableMetrics) {
      this.setupPerformanceMetrics();
    }

    if (this.options.enableErrorHandling) {
      this.setupErrorHandling();
    }

    if (this.options.enableResourceHints) {
      this.optimizeResourceLoading();
    }

    this.setupMemoryOptimization();
    this.monitorWebVitals();
  }

  setupPerformanceMetrics() {
    // Monitor page load performance
    window.addEventListener('load', () => {
      if ('performance' in window) {
        const perfData = performance.getEntriesByType('navigation')[0];

        this.metrics.pageLoad = {
          domContentLoaded: perfData.domContentLoadedEventEnd - perfData.domContentLoadedEventStart,
          loadComplete: perfData.loadEventEnd - perfData.loadEventStart,
          firstPaint: this.getFirstPaint(),
          firstContentfulPaint: this.getFirstContentfulPaint(),
          timestamp: Date.now()
        };

        this.reportMetrics();
      }
    });

    // Monitor resource loading
    if ('PerformanceObserver' in window) {
      const resourceObserver = new PerformanceObserver((list) => {
        const entries = list.getEntries();
        entries.forEach(entry => {
          if (entry.duration > 1000) { // Resources taking more than 1s
            console.warn(`Slow resource: ${entry.name} took ${entry.duration}ms`);
          }
        });
      });

      resourceObserver.observe({ entryTypes: ['resource'] });
    }
  }

  setupErrorHandling() {
    // Global error handler
    window.addEventListener('error', (event) => {
      const error = {
        message: event.message,
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
        stack: event.error?.stack,
        timestamp: Date.now(),
        userAgent: navigator.userAgent,
        url: window.location.href
      };

      this.metrics.errors.push(error);
      this.handleError(error);
    });

    // Unhandled promise rejections
    window.addEventListener('unhandledrejection', (event) => {
      const error = {
        type: 'unhandledrejection',
        reason: event.reason,
        promise: event.promise,
        timestamp: Date.now(),
        url: window.location.href
      };

      this.metrics.errors.push(error);
      this.handleError(error);
    });

    // Resource loading errors
    document.addEventListener('error', (event) => {
      if (event.target !== window) {
        const error = {
          type: 'resource',
          element: event.target.tagName,
          source: event.target.src || event.target.href,
          timestamp: Date.now(),
          url: window.location.href
        };

        this.metrics.errors.push(error);
        this.handleResourceError(event.target);
      }
    }, true);
  }

  optimizeResourceLoading() {
    // Add resource hints for better performance
    this.addDNSPrefetch();
    this.addPreloadHints();
    this.setupIntersectionObserver();
  }

  addDNSPrefetch() {
    const externalDomains = [
      'fonts.googleapis.com',
      'fonts.gstatic.com',
      'cdnjs.cloudflare.com'
    ];

    externalDomains.forEach(domain => {
      const link = document.createElement('link');
      link.rel = 'dns-prefetch';
      link.href = `//${domain}`;
      document.head.appendChild(link);
    });
  }

  addPreloadHints() {
    // Preload critical resources
    const criticalResources = [
      { href: '/assets/fonts/inter-regular.woff2', as: 'font', type: 'font/woff2' },
      { href: '/css/main.css', as: 'style' }
    ];

    criticalResources.forEach(resource => {
      const existingLink = document.querySelector(`link[href="${resource.href}"]`);
      if (!existingLink) {
        const link = document.createElement('link');
        link.rel = 'preload';
        link.href = resource.href;
        link.as = resource.as;
        if (resource.type) link.type = resource.type;
        if (resource.as === 'font') link.crossOrigin = 'anonymous';
        document.head.appendChild(link);
      }
    });
  }

  setupIntersectionObserver() {
    // Optimize animations and content loading
    if ('IntersectionObserver' in window) {
      const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            // Start animations or load content when visible
            entry.target.classList.add('in-viewport');

            // Load deferred content
            this.loadDeferredContent(entry.target);
          } else {
            // Pause animations when not visible
            entry.target.classList.remove('in-viewport');
          }
        });
      }, {
        rootMargin: '50px'
      });

      // Observe sections for performance optimization
      const sections = document.querySelectorAll('section');
      sections.forEach(section => observer.observe(section));
    }
  }

  setupMemoryOptimization() {
    // Clean up unused event listeners and DOM references
    window.addEventListener('beforeunload', () => {
      this.cleanup();
    });

    // Monitor memory usage if available
    if ('memory' in performance) {
      setInterval(() => {
        const memory = performance.memory;
        if (memory.usedJSHeapSize > memory.jsHeapSizeLimit * 0.9) {
          console.warn('High memory usage detected, cleaning up...');
          this.forceCleanup();
        }
      }, 30000); // Check every 30 seconds
    }
  }

  monitorWebVitals() {
    // Monitor Core Web Vitals
    if ('PerformanceObserver' in window) {
      // Largest Contentful Paint
      const lcpObserver = new PerformanceObserver((list) => {
        const entries = list.getEntries();
        const lastEntry = entries[entries.length - 1];
        this.metrics.lcp = lastEntry.startTime;

        if (lastEntry.startTime > 2500) {
          console.warn(`LCP is ${lastEntry.startTime}ms (should be < 2500ms)`);
        }
      });
      lcpObserver.observe({ entryTypes: ['largest-contentful-paint'] });

      // First Input Delay
      const fidObserver = new PerformanceObserver((list) => {
        const entries = list.getEntries();
        entries.forEach(entry => {
          this.metrics.fid = entry.processingStart - entry.startTime;

          if (entry.processingStart - entry.startTime > 100) {
            console.warn(`FID is ${entry.processingStart - entry.startTime}ms (should be < 100ms)`);
          }
        });
      });
      fidObserver.observe({ entryTypes: ['first-input'] });

      // Cumulative Layout Shift
      let clsScore = 0;
      const clsObserver = new PerformanceObserver((list) => {
        const entries = list.getEntries();
        entries.forEach(entry => {
          if (!entry.hadRecentInput) {
            clsScore += entry.value;
            this.metrics.cls = clsScore;

            if (clsScore > 0.1) {
              console.warn(`CLS is ${clsScore} (should be < 0.1)`);
            }
          }
        });
      });
      clsObserver.observe({ entryTypes: ['layout-shift'] });
    }
  }

  getFirstPaint() {
    const paintEntries = performance.getEntriesByType('paint');
    const fpEntry = paintEntries.find(entry => entry.name === 'first-paint');
    return fpEntry ? fpEntry.startTime : null;
  }

  getFirstContentfulPaint() {
    const paintEntries = performance.getEntriesByType('paint');
    const fcpEntry = paintEntries.find(entry => entry.name === 'first-contentful-paint');
    return fcpEntry ? fcpEntry.startTime : null;
  }

  handleError(error) {
    // Log error for development
    console.error('Application Error:', error);

    // Show user-friendly error message for critical errors
    if (this.isCriticalError(error)) {
      this.showErrorMessage('Something went wrong. Please refresh the page.');
    }

    // Report to analytics service if configured
    if (this.options.reportingEndpoint) {
      this.reportError(error);
    }
  }

  handleResourceError(element) {
    // Handle different types of resource errors
    switch (element.tagName.toLowerCase()) {
    case 'img':
      this.handleImageError(element);
      break;
    case 'script':
      console.error(`Failed to load script: ${element.src}`);
      break;
    case 'link':
      console.error(`Failed to load stylesheet: ${element.href}`);
      break;
    }
  }

  handleImageError(img) {
    // Replace with placeholder or hide broken image
    if (!img.dataset.errorHandled) {
      img.dataset.errorHandled = 'true';
      img.style.display = 'none';

      // Optional: Replace with placeholder
      const placeholder = document.createElement('div');
      placeholder.className = 'image-placeholder';
      placeholder.textContent = 'Image unavailable';
      img.parentNode?.insertBefore(placeholder, img);
    }
  }

  loadDeferredContent(element) {
    // Load content that was deferred for performance
    const deferredImages = element.querySelectorAll('img[data-src]');
    deferredImages.forEach(img => {
      if (!img.src && img.dataset.src) {
        img.src = img.dataset.src;
        img.removeAttribute('data-src');
      }
    });
  }

  isCriticalError(error) {
    // Determine if error is critical enough to show to user
    const criticalKeywords = ['TypeError', 'ReferenceError', 'network'];
    return criticalKeywords.some(keyword =>
      error.message?.includes(keyword) || error.type?.includes(keyword)
    );
  }

  showErrorMessage(message) {
    // Create user-friendly error notification
    const errorDiv = document.createElement('div');
    errorDiv.className = 'error-notification';
    errorDiv.innerHTML = `
      <div class="error-content">
        <span>${message}</span>
        <button onclick="this.parentElement.parentElement.remove()" aria-label="Close error">×</button>
      </div>
    `;

    errorDiv.style.cssText = `
      position: fixed;
      top: 20px;
      right: 20px;
      background: #f8d7da;
      color: #721c24;
      padding: 12px 16px;
      border: 1px solid #f5c6cb;
      border-radius: 4px;
      z-index: 10000;
      max-width: 300px;
    `;

    document.body.appendChild(errorDiv);

    // Auto-remove after 5 seconds
    setTimeout(() => {
      if (errorDiv.parentNode) {
        errorDiv.remove();
      }
    }, 5000);
  }

  reportError(error) {
    // Send error to reporting endpoint
    if (navigator.sendBeacon) {
      const data = JSON.stringify({
        error,
        timestamp: Date.now(),
        url: window.location.href,
        userAgent: navigator.userAgent
      });

      navigator.sendBeacon(this.options.reportingEndpoint, data);
    } else {
      // Fallback to fetch
      fetch(this.options.reportingEndpoint, {
        method: 'POST',
        body: JSON.stringify({ error }),
        headers: { 'Content-Type': 'application/json' },
        keepalive: true
      }).catch(() => {
        // Silently fail if reporting fails
      });
    }
  }

  reportMetrics() {
    // Report performance metrics
    if (this.options.reportingEndpoint) {
      const data = {
        metrics: this.metrics,
        timestamp: Date.now(),
        url: window.location.href
      };

      navigator.sendBeacon?.(
        this.options.reportingEndpoint + '/metrics',
        JSON.stringify(data)
      );
    }
  }

  forceCleanup() {
    // Force garbage collection and cleanup
    if (window.gc) {
      window.gc();
    }

    // Remove unused event listeners
    const unusedElements = document.querySelectorAll('[data-cleanup="true"]');
    unusedElements.forEach(el => el.remove());
  }

  cleanup() {
    // Cleanup before page unload
    this.reportMetrics();

    // Clear intervals and observers
    if (this.performanceObserver) {
      this.performanceObserver.disconnect();
    }
  }

  // Public API
  getMetrics() {
    return { ...this.metrics };
  }

  trackUserInteraction(interaction) {
    this.metrics.userInteractions.push({
      ...interaction,
      timestamp: Date.now()
    });
  }

  optimizeImages() {
    // Convert images to WebP if supported
    if (this.supportsWebP()) {
      const images = document.querySelectorAll('img[src$=".jpg"], img[src$=".png"]');
      images.forEach(img => {
        const webpSrc = img.src.replace(/\.(jpg|png)$/, '.webp');

        // Check if WebP version exists
        const testImg = new Image();
        testImg.onload = () => {
          img.src = webpSrc;
        };
        testImg.src = webpSrc;
      });
    }
  }

  supportsWebP() {
    const canvas = document.createElement('canvas');
    canvas.width = 1;
    canvas.height = 1;
    return canvas.toDataURL('image/webp').indexOf('data:image/webp') === 0;
  }

  destroy() {
    // Clean up all observers and listeners
    if (this.performanceObserver) {
      this.performanceObserver.disconnect();
    }

    // Final metrics report
    this.reportMetrics();
  }
}
